import React, { useState, useEffect, useMemo } from "react";
import {
  Table,
  Button,
  message,
  Card,
  Typography,
  Empty,
  Space,
  Form,
  Input,
  Col,
  Select,
} from "antd";
import { EyeOutlined, ReloadOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ColumnsType } from "antd/es/table";
import FilterPanel from "../../components/common/FilterPanel";
import categoryService from "../../services/categoryService";
import dayjs from "dayjs";
import {
  Category,
  CategoryFilters,
  CategoryPaginationParams,
  CategorySortField,
  SortOrder,
} from "../../types/category";
import {
  CATEGORY_PAGINATION_DEFAULT,
  CATEGORY_PAGE_SIZES,
  CATEGORY_COLUMN_WIDTHS,
  CATEGORY_VALIDATION_RULES,
} from "../../utils/categoryConstants";
import "../../styles/container.css";

const { Option } = Select;

const CategoryList: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [form] = Form.useForm();

  // State management
  const [loading, setLoading] = useState<boolean>(false);
  const [categories, setCategories] = useState<Category[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [current, setCurrent] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(20);
  const [filters, setFilters] = useState<CategoryFilters>({});
  const [sortField, setSortField] = useState<CategorySortField>();
  const [sortOrder, setSortOrder] = useState<"ascend" | "descend">("descend");

  // Dropdown options
  const [divisionOptions, setDivisionOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [groupOptions, setGroupOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [departmentOptions, setDepartmentOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [classOptions, setClassOptions] = useState<
    { label: string; value: string }[]
  >([]);

  // Selected values for cascading
  const [selectedDivision, setSelectedDivision] = useState<string | undefined>(
    undefined
  );
  const [selectedGroup, setSelectedGroup] = useState<string | undefined>(
    undefined
  );
  const [selectedDepartment, setSelectedDepartment] = useState<
    string | undefined
  >(undefined);

  const fetchCategories = async () => {
    setLoading(true);
    try {
      const response = await categoryService.getCategories(filters, {
        currentPage: current,
        pageSize,
        sortBy: sortField,
        sortOrder: sortOrder === "ascend" ? "asc" : "desc",
      });

      if (response.success && response.data) {
        setCategories(response.data.tableData);
        setTotal(response.data.paging.totalCount);
      } else {
        console.error("返回的数据格式不正确:", response);
        message.error(
          response.errorMessage || t("category.messages.fetchError")
        );
        setCategories([]);
        setTotal(0);
      }
    } catch (error) {
      message.error(t("category.messages.fetchError"));
      console.error("获取类目列表失败:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDropdownOptions = async (params?: { searchStr?: string }) => {
    try {
      // Fetch division options
      const divisionResponse = await categoryService.listDivision(params);
      if (divisionResponse.success && divisionResponse.data) {
        setDivisionOptions(divisionResponse.data);
      }
    } catch (error) {
      console.error("获取事业部下拉选项失败:", error);
    }
  };

  // Fetch group options based on selected division
  const fetchGroupOptions = async (
    divisionCode?: string,
    searchStr?: string
  ) => {
    try {
      const groupResponse = await categoryService.listGroup({
        divisionCode,
        searchStr,
      });
      if (groupResponse.success && groupResponse.data) {
        setGroupOptions(groupResponse.data);
      }
    } catch (error) {
      console.error("获取组下拉选项失败:", error);
    }
  };

  // Fetch department options based on selected division and group
  const fetchDepartmentOptions = async (
    divisionCode?: string,
    groupCode?: string,
    searchStr?: string
  ) => {
    try {
      const departmentResponse = await categoryService.listDepartment({
        divisionCode,
        groupCode,
        searchStr,
      });
      if (departmentResponse.success && departmentResponse.data) {
        setDepartmentOptions(departmentResponse.data);
      }
    } catch (error) {
      console.error("获取部门下拉选项失败:", error);
    }
  };

  // Fetch class options based on selected division, group and department
  const fetchClassOptions = async (
    divisionCode?: string,
    groupCode?: string,
    departmentCode?: string,
    searchStr?: string
  ) => {
    try {
      const classResponse = await categoryService.listClass({
        divisionCode,
        groupCode,
        departmentCode,
        searchStr,
      });
      if (classResponse.success && classResponse.data) {
        setClassOptions(classResponse.data);
      }
    } catch (error) {
      console.error("获取类别下拉选项失败:", error);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, [current, pageSize, filters]);

  // 初始化时获取下拉选项
  useEffect(() => {
    fetchDropdownOptions();
  }, []);

  // 使用 useMemo 实现前端排序
  const sortedCategories = useMemo(() => {
    if (!sortField || !sortOrder) {
      return categories;
    }

    return [...categories].sort((a, b) => {
      const aValue = a[sortField];
      const bValue = b[sortField];

      // 处理不同数据类型的比较
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        const result = aValue.localeCompare(bValue);
        return sortOrder === 'ascend' ? result : -result;
      }

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        const result = aValue - bValue;
        return sortOrder === 'ascend' ? result : -result;
      }

      // 对于时间戳字段的特殊处理
      if (sortField === 'gmtCreate' || sortField === 'gmtModified') {
        const result = (aValue as number) - (bValue as number);
        return sortOrder === 'ascend' ? result : -result;
      }

      // 对于其他类型，转换为字符串进行比较
      const result = String(aValue).localeCompare(String(bValue));
      return sortOrder === 'ascend' ? result : -result;
    });
  }, [categories, sortField, sortOrder]);

  const handleTableChange = (pagination: any, _filters: any, sorter: any) => {
    setCurrent(pagination.current);
    setPageSize(pagination.pageSize);

    // 只更新排序状态，不触发后端请求
    if (sorter.field && sorter.order) {
      setSortField(sorter.field);
      setSortOrder(sorter.order);
    } else {
      // 如果没有排序，重置排序状态
      setSortField(undefined);
      setSortOrder(undefined);
    }
  };

  // Format datetime display
  const formatDateTime = (timestamp: number): string => {
    return dayjs(timestamp).format("YYYY-MM-DD HH:mm:ss");
  };

  const columns: ColumnsType<Category> = [
    {
      title: t("category.tableColumns.divisionCode"),
      dataIndex: "divisionCode",
      key: "divisionCode",
      width: 120,
      // sorter: true,
      // sortOrder: sortField === "divisionCode" ? sortOrder : null,
    },
    {
      title: t("category.tableColumns.divisionName"),
      dataIndex: "divisionName",
      key: "divisionName",
      width: 120,
      // sorter: true,
      // sortOrder: sortField === "divisionName" ? sortOrder : null,
    },
    {
      title: t("category.tableColumns.groupCode"),
      dataIndex: "groupCode",
      key: "groupCode",
      width: 120,
      // sorter: true,
      // sortOrder: sortField === "groupCode" ? sortOrder : null,
    },
    {
      title: t("category.tableColumns.groupName"),
      dataIndex: "groupName",
      key: "groupName",
      width: 120,
      // sorter: true,
      // sortOrder: sortField === "groupName" ? sortOrder : null,
    },
    {
      title: t("category.tableColumns.departmentCode"),
      dataIndex: "departmentCode",
      key: "departmentCode",
      width: 120,
      // sorter: true,
      // sortOrder: sortField === "departmentCode" ? sortOrder : null,
    },
    {
      title: t("category.tableColumns.departmentName"),
      dataIndex: "departmentName",
      key: "departmentName",
      width: 120,
      // sorter: true,
      // sortOrder: sortField === "departmentName" ? sortOrder : null,
    },
    {
      title: t("category.tableColumns.classCode"),
      dataIndex: "classCode",
      key: "classCode",
      width: 120,
      // sorter: true,
      // sortOrder: sortField === "classCode" ? sortOrder : null,
    },
    {
      title: t("category.tableColumns.className"),
      dataIndex: "className",
      key: "className",
      width: 120,
      // sorter: true,
      // sortOrder: sortField === "className" ? sortOrder : null,
    },
    {
      title: t("category.tableColumns.subClassCode"),
      dataIndex: "subClassCode",
      key: "subClassCode",
      width: 120,
      // sorter: true,
      // sortOrder: sortField === "subClassCode" ? sortOrder : null,
    },
    {
      title: t("category.tableColumns.subclassName"),
      dataIndex: "subClassName",
      key: "subClassName",
      width: 120,
      // sorter: true,
      // sortOrder: sortField === "subClassName" ? sortOrder : null,
    },
    {
      title: t("category.tableColumns.gmtCreate"),
      dataIndex: "gmtCreate",
      key: "gmtCreate",
      width: 180,
      render: (timestamp: number) => formatDateTime(timestamp),
      // sorter: true,
      // sortOrder: sortField === "gmtCreate" ? sortOrder : null,
    },
    {
      title: t("category.tableColumns.gmtModified"),
      dataIndex: "gmtModified",
      key: "gmtModified",
      width: 180,
      render: (timestamp: number) => formatDateTime(timestamp),
      // sorter: true,
      // sortOrder: sortField === "gmtModified" ? sortOrder : null,
    },
  ];

  // Handle division selection change
  const handleDivisionChange = (value: string) => {
    setSelectedDivision(value);

    // Fetch group options based on selected division
    if (value) {
      fetchGroupOptions(value);
    } else {
      // If no division selected, fetch all groups
      fetchGroupOptions();
    }
  };

  // Handle group selection change
  const handleGroupChange = (value: string) => {
    setSelectedGroup(value);

    // Fetch department options based on selected division and group
    if (value && selectedDivision) {
      fetchDepartmentOptions(selectedDivision, value);
    } else if (selectedDivision) {
      // If no group selected but division is selected, fetch all departments for that division
      fetchDepartmentOptions(selectedDivision);
    } else {
      // If neither division nor group selected, fetch all departments
      fetchDepartmentOptions();
    }
  };

  // Handle department selection change
  const handleDepartmentChange = (value: string) => {
    setSelectedDepartment(value);

    // Fetch class options based on selected division, group and department
    if (value && selectedDivision && selectedGroup) {
      fetchClassOptions(selectedDivision, selectedGroup, value);
    } else if (selectedDivision && selectedGroup) {
      // If no department selected but division and group are selected, fetch all classes for that division and group
      fetchClassOptions(selectedDivision, selectedGroup);
    } else if (selectedDivision) {
      // If only division is selected, fetch all classes for that division
      fetchClassOptions(selectedDivision);
    } else {
      // If none of division, group, or department selected, fetch all classes
      fetchClassOptions();
    }
  };

  // Handle division search
  const handleDivisionSearch = (value: string) => {
    fetchDropdownOptions({ searchStr: value });
  };

  // Handle group search
  const handleGroupSearch = (value: string) => {
    fetchGroupOptions(selectedDivision, value);
  };

  // Handle department search
  const handleDepartmentSearch = (value: string) => {
    fetchDepartmentOptions(selectedDivision, selectedGroup, value);
  };

  // Handle class search
  const handleClassSearch = (value: string) => {
    fetchClassOptions(
      selectedDivision,
      selectedGroup,
      selectedDepartment,
      value
    );
  };

  // console.log(">>>>filters", filters, form.getFieldsValue());

  return (
    <div>
      <FilterPanel
        form={form}
        onFilter={(values) => {
          const newFilters = {
            divisionCode: values.divisionCode || undefined,
            divisionName: values.divisionName?.trim() || undefined,
            groupCode: values.groupCode || undefined,
            groupName: values.groupName?.trim() || undefined,
            departmentCode: values.departmentCode || undefined,
            departmentName: values.departmentName?.trim() || undefined,
            classCode: values.classCode || undefined,
            className: values.className?.trim() || undefined,
            subClassCode: values.subClassCode?.trim() || undefined,
            subClassName: values.subClassName?.trim() || undefined,
            search: values.search?.trim() || undefined,
          };

          setFilters(newFilters);
          setCurrent(1);
        }}
        onReset={() => {
          // 重置表单
          form.resetFields();

          // 重置所有状态
          setFilters({});
          setCurrent(1);
          setSortField(undefined);
          setSortOrder(undefined);
          setSelectedDivision(undefined);
          setSelectedGroup(undefined);
          setSelectedDepartment(undefined);
          // 清空级联选项
          setGroupOptions([]);
          setDepartmentOptions([]);
          setClassOptions([]);
          // 重新获取下拉选项
          fetchDropdownOptions();
        }}
        // initialValues={{
        //   divisionCode: filters.divisionCode || undefined,
        //   groupCode: filters.groupCode || undefined,
        //   departmentCode: filters.departmentCode || undefined,
        //   classCode: filters.classCode || undefined,
        //   subClassCode: filters.subClassCode || "",
        //   subClassName: filters.subClassName || "",
        // }}
        title={t("menu.category")}
      >
        <Col xs={24} sm={12} md={8} lg={6}>
          <Form.Item
            name="divisionCode"
            label={t("category.filter.divisionCode")}
          >
            <Select
              placeholder={t("category.searchPlaceholders.divisionCode")}
              allowClear
              showSearch
              optionFilterProp="children"
              onSearch={handleDivisionSearch}
              onChange={handleDivisionChange}
            >
              {divisionOptions.map((option) => (
                <Option key={option.value} value={option.value}>
                  {option.label}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <Form.Item name="groupCode" label={t("category.filter.groupCode")}>
            <Select
              placeholder={t("category.searchPlaceholders.groupCode")}
              allowClear
              showSearch
              optionFilterProp="children"
              onSearch={handleGroupSearch}
              onChange={handleGroupChange}
            >
              {groupOptions.map((option) => (
                <Option key={option.value} value={option.value}>
                  {option.label}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <Form.Item
            name="departmentCode"
            label={t("category.filter.departmentCode")}
          >
            <Select
              placeholder={t("category.searchPlaceholders.departmentCode")}
              allowClear
              showSearch
              optionFilterProp="children"
              onSearch={handleDepartmentSearch}
              onChange={handleDepartmentChange}
            >
              {departmentOptions.map((option) => (
                <Option key={option.value} value={option.value}>
                  {option.label}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <Form.Item name="classCode" label={t("category.filter.classCode")}>
            <Select
              placeholder={t("category.searchPlaceholders.classCode")}
              allowClear
              showSearch
              optionFilterProp="children"
              onSearch={handleClassSearch}
            >
              {classOptions.map((option) => (
                <Option key={option.value} value={option.value}>
                  {option.label}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <Form.Item
            name="subClassCode"
            label={t("category.filter.subClassCode")}
          >
            <Input
              placeholder={t("category.searchPlaceholders.subClassCode")}
              allowClear
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <Form.Item
            name="subClassName"
            label={t("category.filter.subclassName")}
          >
            <Input
              placeholder={t("category.searchPlaceholders.subclassName")}
              allowClear
            />
          </Form.Item>
        </Col>
      </FilterPanel>

      <Card className="table-card">
        {sortedCategories.length > 0 ? (
          <Table
            rowKey="id"
            columns={columns}
            dataSource={sortedCategories}
            loading={loading}
            pagination={{
              current,
              pageSize,
              total,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total) =>
                t("category.pagination.total", { count: total }),
              pageSizeOptions: CATEGORY_PAGE_SIZES.map((size) =>
                size.toString()
              ),
            }}
            onChange={handleTableChange}
            scroll={{ x: 1800, y: 500 }}
          />
        ) : (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <span>
                {Object.keys(filters).some(
                  (key) =>
                    filters[key as keyof typeof filters] !== "" &&
                    filters[key as keyof typeof filters] !== undefined
                )
                  ? t("category.emptyMessages.noSearchResults")
                  : t("category.emptyMessages.noCategories")}
              </span>
            }
            style={{ margin: "40px 0" }}
          />
        )}
      </Card>
    </div>
  );
};

export default CategoryList;
